import React, { useState } from "react";
import {
  Bid,
  BidsContextModel,
  BidSettings,
  NotificationItems,
} from "../models/Bid";
import {
  acceptBidAPI,
  createLaneAPI,
  deleteLaneAPI,
  fetchAcceptedBids,
  fetchBidSettings,
  fetchLanes,
  fetchNotificationCount,
  fetchPendingBids,
  fetchRejectedBids,
  getNotificationSettings,
  readNotifications,
  rejectBidAPI,
  updateBidSettingsAPI,
  updateLaneAPI,
  updateTermsAPI,
} from "../services/BidsAPI";
import { fetchCountryCode } from "../services/companyAPI";
import { useToast } from "@chakra-ui/react";
import axios, { AxiosError } from "axios";
import { BidStatus, LoadingState } from "../utils/enum";
import { Equipment } from "../models";
import { Lane } from "../models/lane";
import { fakeAPIRequest } from "../utils/helper";
import { Chat } from "../models/Chat";

type BidsProviderProps = {
  children?: React.ReactNode;
};

export const BidsContext = React.createContext<BidsContextModel>(
  {} as BidsContextModel
);

export const BidsProvider = ({ children }: BidsProviderProps) => {
  const [selectedBid, setSelectedBid] = useState<Bid>();
  const [pendingBids, setPendingBids] = useState<Bid[]>([]);
  const [acceptedBids, setAcceptedBids] = useState<Bid[]>([]);
  const [rejectedBids, setRejectedBids] = useState<Bid[]>([]);
  const [searchedBids, setSearchedBids] = useState<Bid[]>([]);
  const toast = useToast();
  const [bidSettings, setBidSettings] = useState<BidSettings>({
    pickupDeadHeadingMiles: 0,
    returnDeadHeadingMiles: 0,
    pricePerMile: 0,
    fuelSurcharge: 0,
    email: "",
    phoneNumber: "",
    net0: 0,
    net15: 0,
    net30: 0,
    net60: 0,
    net90: 0,
  });
  const [isAddingLane, setIsAddingLane] = useState(false);
  const [laneToEdit, setLaneToEdit] = useState<Lane>();
  const [lanes, setLanes] = useState<Lane[]>([]);
  const [acceptedNextPageToken, setAcceptedNextPageToken] = useState<string>();
  const [rejectedNextPageToken, setRejectedNextPageToken] = useState<string>();
  const [bidsTabViewIndex, setBidsTabViewIndex] = useState(0);
  const [bidNotifications, setBidNotifications] = useState<{
    unreadBids: { bidID: string; companyID: string }[];
  }>({ unreadBids: [] });
  const [countryCode, setCountryCode] = useState<string>();
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingState, setLoadingState] = useState<LoadingState>(
    LoadingState.Pending
  );
  const [loadingNotif, setLoadingNotif] = useState(LoadingState.None);
  const [notificationSettings, setNotificationSettings] =
    useState<NotificationItems>();
  const [loadingDetailsState, setLoadingDetailsState] = useState<LoadingState>(
    LoadingState.Pending
  );
  const [loadingAcceptedState, setLoadingAcceptedState] = useState(
    LoadingState.Pending
  );
  const [loadingRejectedState, setLoadingRejectedState] = useState(
    LoadingState.Pending
  );
  const [detailDrawer, setDetailDrawer] = useState(false);

  const getUserCountryCode = async () => {
    const code = await fetchCountryCode();
    setCountryCode(code);
  };

  const listNotificationsSettings = async () => {
    try {
      setLoadingNotif(LoadingState.Pending);
      const notif = await getNotificationSettings();
      setNotificationSettings(notif);
      setLoadingNotif(LoadingState.Success);
    } catch (error) {
      setLoadingState(LoadingState.Error);
      handleToastError({
        title: `Failed to load settings.`,
        description: `We couldn't load your bid settings. Please try again later.`,
        error,
      });
    }
  };

  const countBidNotifications = async () => {
    const response = await fetchNotificationCount();
    console.log("notifications response", response);
    setBidNotifications({ unreadBids: response.data.bidIDs });
  };

  const readBidNotifications = async () => {
    const { unreadBids } = bidNotifications;
    if (unreadBids.length > 0) {
      await readNotifications({ bidIDs: unreadBids });
      setBidNotifications({ unreadBids: [] });
    }
  };

  const changeBidTabView = (tabIndex: number) => {
    setBidsTabViewIndex(tabIndex);
    setSearchedBids([]);
    setSearchTerm("");
  };

  const searchBids = async (term: string) => {
    try {
      term = term.trim().toLowerCase();

      const bids = [
        ...(bidsTabViewIndex === 0
          ? pendingBids
          : bidsTabViewIndex === 1
          ? acceptedBids
          : rejectedBids),
      ];

      // Implement search locally
      const searchedItems =
        term.length === 0
          ? []
          : bids.filter((bid) => {
              const isValid =
                bid.shipperName?.toLowerCase().includes(term) ||
                bid.distance.toString().toLowerCase().includes(term) ||
                bid.loadData.pickupLocation.name.toLowerCase().includes(term) ||
                bid.loadData.dropOffLocation.name
                  .toLowerCase()
                  .includes(term) ||
                bid.totalRevenue.toString().toLowerCase().includes(term) ||
                bid.askPrice.toString().toLowerCase().includes(term) ||
                bid.matchPrice.toString().toLowerCase().includes(term);
              return isValid;
            });

      setSearchTerm(term);
      setSearchedBids(searchedItems);
    } catch (error) {
      console.error("error searhing", error);
    }
  };

  const listPendingBids = async () => {
    try {
      setLoadingState(LoadingState.Pending);
      // await readBidNotifications();
      const data = await fetchPendingBids();

      const pending: Bid[] = [];
      const accepted: Bid[] = [];
      const rejected: Bid[] = [];

      data.forEach((bid) => {
        if (bid.status === "pending") {
          const expiryDate = new Date(bid.expiresAt * 1000); // Convert seconds to milliseconds
          console.log("pending date", {
            expiryDate: expiryDate.toISOString(),
            expiresAt: bid.expiresAt,
            now: new Date().toISOString(),
            bidData: bid.loadData.dropOffLocation,
          });
          const diffInMs = expiryDate.getTime() - new Date().getTime();
          console.log("diffs", diffInMs);
          if (diffInMs > 0) pending.push(bid);
          else rejected.push({ ...bid, status: "expired" });
        } else if (bid.status === "accepted") {
          accepted.push(bid);
        } else {
          rejected.push(bid);
        }
      });

      setPendingBids(pending);
      setAcceptedBids(accepted);
      setRejectedBids(rejected);

      setLoadingState(LoadingState.Success);
    } catch (e) {
      console.error("failed to retrieve bids", e);
      setLoadingState(LoadingState.Error);
    }
  };

  const listAcceptedBids = async ({
    isLoadingMore,
  }: {
    isLoadingMore: boolean;
  }) => {
    try {
      if (!isLoadingMore) {
        setAcceptedBids([]);
      }
      setLoadingAcceptedState(LoadingState.Pending);
      const data = await fetchAcceptedBids({
        nextPageToken: acceptedNextPageToken,
      });
      setAcceptedBids(data);
      setLoadingAcceptedState(LoadingState.Success);
    } catch (e) {
      setLoadingAcceptedState(LoadingState.Error);
    }
  };

  const listRejectedBids = async ({
    isLoadingMore,
  }: {
    isLoadingMore: boolean;
  }) => {
    try {
      setLoadingRejectedState(LoadingState.Pending);

      if (!isLoadingMore) {
        setRejectedBids([]);
      }

      const data = await fetchRejectedBids({
        nextPageToken: rejectedNextPageToken,
      });

      // const { items, nextPageToken } = response.data;

      // const encodedToken = nextPageToken
      //   ? encodeURIComponent(JSON.stringify(nextPageToken))
      //   : undefined;
      // setRejectedNextPageToken(encodedToken);

      // setRejectedBids(isLoadingMore ? [...rejectedBids, ...items] : items);
      setRejectedBids(data);
      setLoadingRejectedState(LoadingState.Success);
    } catch (e) {
      setLoadingRejectedState(LoadingState.Error);
    }
  };

  const listBidSettings = async () => {
    if (
      countryCode &&
      (bidSettings.email ||
        bidSettings.pickupDeadHeadingMiles ||
        bidSettings.returnDeadHeadingMiles ||
        bidSettings.phoneNumber)
    ) {
      setLoadingState(LoadingState.Success);
      return;
    }

    try {
      setLoadingState(LoadingState.Pending);
      const response = await fetchBidSettings();
      const ctryCode = await fetchCountryCode();
      const result = response.data ?? {};
      const settings: BidSettings = {
        pricePerMile: result.pricePerMile ?? 0,
        pickupDeadHeadingMiles: result.pickupDeadheadingMiles ?? 0,
        returnDeadHeadingMiles: result.returnDeadHeadingMiles ?? 0,
        fuelSurcharge: result.fuelSurcharge ?? 0,
        net0: result.net0 ?? 0,
        net15: result.net15 ?? 0,
        net30: result.net30 ?? 0,
        net60: result.net60 ?? 0,
        net90: result.net90 ?? 0,
        // email: result.notifEmail ?? "",
        // phoneNumber: result.notifPhoneNumber ?? "",
        email: "",
        phoneNumber: "",
      };
      setCountryCode(ctryCode);
      setBidSettings(settings);
      setLoadingState(LoadingState.Success);
    } catch (error) {
      setLoadingState(LoadingState.Error);
      handleToastError({
        title: `Failed to load settings.`,
        description: `We couldn't load your bid settings. Please try again later.`,
        error,
      });
    }
  };

  const acceptBid = async ({
    bidID,
    loadID,
    shipperID,
  }: {
    bidID: string;
    loadID: string;
    shipperID: string;
  }) => {
    const createdChat = await acceptBidAPI(loadID, shipperID);
    const currentBid = pendingBids.find((item) => item.id === bidID);
    if (currentBid) {
      const updatedBids = pendingBids.filter((item) => item.id !== bidID);
      setPendingBids(updatedBids);
      currentBid.status = BidStatus.Accepted;
      currentBid.updatedAt = new Date().getTime() / 1000;
      setAcceptedBids([currentBid, ...acceptedBids]);
    }
    setBidsTabViewIndex(1);
    return createdChat;
  };

  const rejectBid = async (
    bidID: string,
    loadID: string,
    shipperID: string
  ) => {
    await rejectBidAPI(loadID, shipperID);
    const currentBid = pendingBids.find((item) => item.id === bidID);
    if (currentBid) {
      const updatedBids = pendingBids.filter((item) => item.id !== bidID);
      setPendingBids(updatedBids);
      currentBid.status = BidStatus.Rejected;
      setRejectedBids([currentBid, ...acceptedBids]);
      currentBid.updatedAt = new Date().getTime() / 1000;
    }
    setBidsTabViewIndex(2);
  };

  const updateBidSettings = async (settings: any) => {
    try {
      await updateBidSettingsAPI(settings);
      setBidSettings({ ...bidSettings, ...settings });
      toast({
        title: "Bid settings updated successfully.",
        position: "top",
        status: "success",
        isClosable: true,
      });
    } catch (error) {
      handleToastError({
        title: `Failed to update settings.`,
        description: `We couldn't update your bid settings. Please try again later.`,
        error,
      });
    }
  };

  const updateTermsSettings = async (termSettings: any) => {
    try {
      await updateTermsAPI(termSettings);
      setBidSettings({
        ...bidSettings,
        ...termSettings,
      });
      toast({
        title: "Terms settings updated successfully.",
        position: "top",
        status: "success",
        isClosable: true,
      });
    } catch (error) {
      handleToastError({
        title: `Failed to update terms settings.`,
        description: `We couldn't update your terms settings. Please try again later.`,
        error,
      });
    }
  };

  const updateNotifications = (notifData: NotificationItems) => {
    setNotificationSettings(notifData);
  };

  const updateBidEquipment = (bid: Bid, equipment: Equipment) => {
    const bids = [...acceptedBids];
    const index = bids.findIndex((item) => item.id === bid.id);
    if (index > -1) {
      bids[index].equipment = equipment;
      setAcceptedBids(bids);
    }
  };

  const listLanes = async () => {
    if (lanes.length > 0) {
      setLoadingState(LoadingState.Success);
      return;
    }
    try {
      setLoadingState(LoadingState.Pending);
      const data = await fetchLanes();
      setLanes(data.lanes);
      setLoadingState(LoadingState.Success);
    } catch (error) {
      setLoadingState(LoadingState.Error);
      handleToastError({
        title: `Failed to load lanes.`,
        description: `We couldn't load your lanes. Please try again later.`,
        error,
      });
    }
  };

  const addLane = async (lane: Lane) => {
    try {
      const id = await createLaneAPI({
        ...lane,
        startDate: lane.startDate?.toISOString(),
        endDate: lane.endDate?.toISOString(),
      });
      lane.laneID = id;
      setLanes([...lanes, lane]);
    } catch (error) {
      setLoadingState(LoadingState.Error);
      throw error;
      // handleToastError({
      //   title: `Failed to add lane.`,
      //   description: `We couldn't add your lane. Please try again later.`,
      //   error,
      // });
    }
  };

  const updateLane = async (editedLane: Lane) => {
    try {
      await updateLaneAPI({
        ...editedLane,
        startDate: editedLane?.startDate?.toISOString(),
        endDate: editedLane?.endDate?.toISOString(),
      });
      const index = lanes.findIndex(
        (lane) => lane.laneID === editedLane.laneID
      );
      if (index > -1) {
        const items = [...lanes];
        items[index] = editedLane;
        setLanes(items);
      }
    } catch (error) {
      setLoadingState(LoadingState.Error);
      throw error;
      // handleToastError({
      //   title: `Failed to update lane.`,
      //   description: `We couldn't update your lane. Please try again later.`,
      //   error,
      // });
    }
  };

  const deleteLane = async (laneID: string) => {
    try {
      await deleteLaneAPI(laneID);
      const index = lanes.findIndex((lane) => lane.laneID === laneID);
      if (index > -1) {
        const items = [...lanes];
        items.splice(index, 1);
        setLanes(items);
      }
    } catch (error) {
      setLoadingState(LoadingState.Error);
      throw error;
      // handleToastError({
      //   title: `Failed to delete lane.`,
      //   description: `We couldn't delete your lane. Please try again later.`,
      //   error,
      // });
    }
  };

  const handleToastError = ({
    title,
    description,
    error,
  }: {
    error: any;
    title: string;
    description: string;
  }) => {
    const isAxiosError = axios.isAxiosError(error);

    toast({
      title: title,
      position: "top",
      status: "error",
      isClosable: true,
      description: isAxiosError
        ? (error as AxiosError<any>).response?.data?.message ?? description
        : description,
    });
  };

  const values: BidsContextModel = {
    selectedBid,
    loadingNotif,
    laneToEdit,
    detailDrawer,
    searchTerm,
    pendingBids,
    acceptedBids,
    countryCode,
    rejectedBids,
    bidNotifications,
    bidSettings,
    loadingState,
    bidsTabViewIndex,
    loadingDetailsState,
    loadingAcceptedState,
    loadingRejectedState,
    rejectedNextPageToken,
    acceptedNextPageToken,
    searchedBids,
    lanes,
    isAddingLane,
    notificationSettings,
    listNotificationsSettings,
    updateNotifications,
    setIsAddingLane,
    listLanes,
    addLane,
    deleteLane,
    updateLane,
    updateBidEquipment,
    searchBids,
    setLaneToEdit,
    listBidSettings,
    changeBidTabView,
    setSelectedBid,
    setLoadingAcceptedState,
    setLoadingDetailsState,
    setLoadingRejectedState,
    setLoadingState,
    updateBidSettings,
    updateTermsSettings,
    countBidNotifications,
    readBidNotifications,
    getUserCountryCode,
    setBidsTabViewIndex,
    listAcceptedBids,
    listRejectedBids,
    listPendingBids,
    acceptBid,
    setDetailDrawer,
    rejectBid,
  };

  return <BidsContext.Provider value={values}>{children}</BidsContext.Provider>;
};
